import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { cn } from '../../utils/css';
import NumberFlow from '@number-flow/react';

interface DualRangeSliderProps
  extends Omit<React.ComponentProps<typeof SliderPrimitive.Root>, 'value'> {
  labelPosition?: 'top' | 'bottom' | 'static';
  lableContenPos?: 'left' | 'right';
  label?: React.ReactNode | ((value: number) => React.ReactNode);
  value?: number[];
  showPlusForMax?: boolean;
  formatNumber?: (value: number) => string | number;
  disableNumberFormatting?: boolean;
}

const DualRangeSlider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  DualRangeSliderProps
>(
  (
    {
      className,
      label,
      labelPosition = 'bottom',
      lableContenPos = 'right',
      showPlusForMax = false,
      formatNumber,
      disableNumberFormatting = false,
      ...props
    },
    ref
  ) => {
    const currentValue = Array.isArray(props.value)
      ? props.value.map(v => (v === null || v === undefined || isNaN(v)) ? 0 : v)
      : [props.min || 0, props.max || 100];
      

    // Function to determine if we should show the plus sign
    const shouldShowPlus = (value: number, index: number) => {
      return showPlusForMax && index === 1 && value === props.max;
    };

    // Function to format the number based on the formatNumber prop
    const formatValue = (value: number) => {
      // Add safety check for null/undefined values
      if (value === null || value === undefined || typeof value !== 'number' || isNaN(value)) {
        return 0;
      }
      
      if (formatNumber) {
        return formatNumber(value);
      }
      return value;
    };

    // Render the number value with or without NumberFlow
    const renderNumberValue = (value: number) => {
      // Add safety check for null/undefined values
      const safeValue = (value === null || value === undefined || typeof value !== 'number' || isNaN(value)) ? 0 : value;
      
      if (disableNumberFormatting) {
        return formatValue(safeValue);
      }
      
      return (
        <NumberFlow
          willChange
          value={formatValue(safeValue)}
          isolate
          opacityTiming={{
            duration: 250,
            easing: 'ease-out',
          }}
          transformTiming={{
            easing: `linear(0, 0.0033 0.8%, 0.0263 2.39%, 0.0896 4.77%, 0.4676 15.12%, 0.5688, 0.6553, 0.7274, 0.7862, 0.8336 31.04%, 0.8793, 0.9132 38.99%, 0.9421 43.77%, 0.9642 49.34%, 0.9796 55.71%, 0.9893 62.87%, 0.9952 71.62%, 0.9983 82.76%, 0.9996 99.47%)`,
            duration: 500,
          }}
        />
      );
    };

    return (
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          'relative flex w-full touch-none select-none items-center text-sm text-muted-foreground',
          className
        )}
        {...props}
        value={currentValue}
      >
        <SliderPrimitive.Track className='relative h-1.5 w-full grow overflow-hidden rounded-full bg-orange-500/20'>
          <SliderPrimitive.Range className='absolute h-full bg-orange-500' />
        </SliderPrimitive.Track>
        <>
          {currentValue.map((value, index) => (
            <React.Fragment key={index}>
              <SliderPrimitive.Thumb className='block h-4 w-4 rounded-full border border-gray-primary/50 bg-white shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50'>
                {label && labelPosition !== 'static' && (
                  <div
                    className={cn(
                      'absolute flex w-full justify-center items-start gap-0.5',
                      labelPosition === 'top' && '-top-6',
                      labelPosition === 'bottom' && 'top-4'
                    )}
                  >
                    {lableContenPos === 'left' && (
                      <>
                        {typeof label === 'function' ? (
                          <span className='inline-block  -translate-y-0.5'>
                            {label(value)}
                          </span>
                        ) : (
                          label && <span className='inline-block '>{label}</span>
                        )}
                      </>
                    )}
                    <div className="flex items-center">
                      {renderNumberValue(value)}
                      {shouldShowPlus(value, index) && <span>+</span>}
                    </div>
                    {lableContenPos === 'right' && (
                      <>
                        {typeof label === 'function' ? (
                          <span className='inline-block  -translate-y-1'>
                            {label(value)}
                          </span>
                        ) : (
                          label && <span className='inline-block '>{label}</span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </SliderPrimitive.Thumb>
            </React.Fragment>
          ))}
        </>

        {label && labelPosition === 'static' && (
          <>
            {currentValue.map((value, index) => (
              <div
                key={index}
                className={cn(
                  'absolute -top-6 w-fit right-0 flex  justify-center items-start gap-0.5'
                )}
              >
                {lableContenPos === 'left' && (
                  <>
                    {typeof label === 'function' ? (
                      <span className='inline-block  -translate-y-0.5'>
                        {label(value)}
                      </span>
                    ) : (
                      label && <span className='inline-block '>{label}</span>
                    )}
                  </>
                )}
                <div className="flex items-center">
                  {renderNumberValue(value)}
                  {shouldShowPlus(value, index) && <span>+</span>}
                </div>
                {lableContenPos === 'right' && (
                  <>
                    {typeof label === 'function' ? (
                      <span className='inline-block  -translate-y-1'>
                        {label(value)}
                      </span>
                    ) : (
                      label && <span className='inline-block '>{label}</span>
                    )}
                  </>
                )}
              </div>
            ))}
          </>
        )}
      </SliderPrimitive.Root>
    );
  }
);
DualRangeSlider.displayName = 'DualRangeSlider';

export { DualRangeSlider };